import { useReducedMotion, type TargetAndTransition, type Transition, type Variants } from 'framer-motion';

/**
 * Shared entrance animation presets
 *
 * Gentle appearance animations used across the portal (stagger, fade, slide-up),
 * each with a reduced-motion mirror (opacity-only, <= 0.4s). Consumers select
 * presets through useAppearVariants() — or the pure getAppearVariants() — so
 * users with `prefers-reduced-motion` never receive transform/blur/scale
 * entrances (A11Y-02).
 */

// --- Types ---

/** Spreadable initial/animate/transition preset for a standalone motion element. */
export interface AppearPreset {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  transition: Transition;
}

/** The full set of shared entrance presets for one motion preference. */
export interface AppearVariantSet {
  /** Stagger parent for lists of `item` children (variants pattern). */
  container: Variants;
  /** Staggered child of `container` (variants pattern). */
  item: Variants;
  /** Standalone page/card entrance (spread onto a motion element). */
  single: AppearPreset;
  /** Route-level page transition (AnimatedOutlet). */
  page: AppearPreset;
  /** Dashboard quote hero cinematic (LeadershipQuotes). */
  quoteHero: AppearPreset;
}

// --- Full-motion presets ---

const APPEAR_CONTAINER: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.05
    }
  }
};

const APPEAR_ITEM: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};

const APPEAR_SINGLE: AppearPreset = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  // WHY: FM12 types `ease` as a 4-tuple bezier; a bare number[] no longer assigns when spread.
  transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] }
};

const PAGE_APPEAR: AppearPreset = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
};

// WHY: 1.5s matches LeadershipQuotes' timer-line reveal (d1_AppearMs) so the quote
// and its timer lines finish together.
const QUOTE_HERO_APPEAR: AppearPreset = {
  initial: { opacity: 0, filter: 'blur(10px)', scale: 1.05 },
  animate: { opacity: 1, filter: 'blur(0px)', scale: 1 },
  transition: { duration: 1.5, ease: 'easeOut' }
};

// --- Reduced-motion mirrors ---

// CONSTRAINT: Mirrors must reveal the same content with opacity-only fades that
// finish within 0.4s — no transform/blur/scale, no stagger, never hidden content.
const REDUCED_FADE: Transition = { duration: 0.25, ease: 'easeOut' };

const APPEAR_CONTAINER_REDUCED: Variants = {
  hidden: { opacity: 0 },
  // WHY: no staggerChildren — children reveal together inside one short fade.
  visible: { opacity: 1, transition: REDUCED_FADE }
};

const APPEAR_ITEM_REDUCED: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: REDUCED_FADE }
};

const APPEAR_SINGLE_REDUCED: AppearPreset = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: REDUCED_FADE
};

const PAGE_APPEAR_REDUCED: AppearPreset = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2, ease: 'easeOut' }
};

const QUOTE_HERO_APPEAR_REDUCED: AppearPreset = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.3, ease: 'easeOut' }
};

// --- Selection ---

const FULL_MOTION_SET: AppearVariantSet = {
  container: APPEAR_CONTAINER,
  item: APPEAR_ITEM,
  single: APPEAR_SINGLE,
  page: PAGE_APPEAR,
  quoteHero: QUOTE_HERO_APPEAR
};

const REDUCED_MOTION_SET: AppearVariantSet = {
  container: APPEAR_CONTAINER_REDUCED,
  item: APPEAR_ITEM_REDUCED,
  single: APPEAR_SINGLE_REDUCED,
  page: PAGE_APPEAR_REDUCED,
  quoteHero: QUOTE_HERO_APPEAR_REDUCED
};

/**
 * Pure preset selection: returns the reduced-motion mirror set when the user
 * prefers reduced motion, the full cinematic set otherwise. Referentially
 * stable — both sets are module constants, safe for render-time selection.
 */
export function getAppearVariants(shouldReduceMotion: boolean): AppearVariantSet {
  return shouldReduceMotion ? REDUCED_MOTION_SET : FULL_MOTION_SET;
}

/**
 * Selects the shared entrance presets honouring `prefers-reduced-motion`.
 * The only sanctioned way to consume the appear presets — do not re-export
 * the raw constants, or reduced-motion users lose the mirror.
 */
export function useAppearVariants(): AppearVariantSet {
  return getAppearVariants(!!useReducedMotion());
}
